import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pager } from "@/app/Pager";
import { useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import { validMcc, validMnc } from "@/app/form";
import { accounts, channels, customers } from "@/app/master";
import { ClearFilters } from "@/app/ClearFilters";
import { ShowPicker } from "@/app/ShowPicker";
import { ToolIcon } from "@/app/toolIcons";
import { messageLines, messages, type MessageCursor } from "@/app/messages";
import { useCanWrite } from "@/app/auth";
import { useTaskCenter } from "@/app/taskCenterContext";
import { accountOptions, idOptions, loadingOf, useList, useNames } from "@/app/queries";
import { totalOf, useNextPage } from "@/app/usePage";
import { useRange } from "@/app/useRange";
import { useShown } from "@/app/useShown";
import { Alert, Button, Combobox, DateRange, EmptyState, FILTER_W, formatTime, Input, MESSAGE_STATE, PageHeader, Select, Table, TableWrap, Th, Toolbar, type MessageState } from "@/ui";
import { MessageDetail } from "./MessageDetail";
import { MESSAGE_COLUMNS, MessageLineRow, type MessageColumn } from "./MessageLineRow";
import { MessageExportForm } from "./MessageExportForm";

/** 时间范围上限，与服务端一致（决策 D82）。 */
const MAX_RANGE_MS = 90 * 24 * 3600 * 1000;

const DEFAULT_RANGE = { preset: "12h" } as const;
const EMPTY_TEXT = { messageId: "", msisdn: "", mcc: "", mnc: "", minAttempts: "" };
// aggregate 为空是聚合重投（一条消息一行），"each" 是每次尝试一行（决策 D187）
const EMPTY_PICK = { customer: "", account: "", channel: "", finalChannel: "", status: "", aggregate: "" };

const STATUS_OPTIONS = [{ value: "", label: "全部状态" }, ...(Object.keys(MESSAGE_STATE) as MessageState[]).map((s) => ({ value: s, label: MESSAGE_STATE[s].label }))];
const AGGREGATE_OPTIONS = [{ value: "", label: "重投聚合" }, { value: "each", label: "重投不聚合" }];

type FilterKey = "range" | keyof typeof EMPTY_TEXT | keyof typeof EMPTY_PICK;

/** 可显示的筛选条件，按筛选栏里的顺序；缺省显示 DEFAULT_FILTERS，其余从“筛选条件”里勾出来（#513，决策 D187）。 */
const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "range", label: "时间" }, { key: "messageId", label: "message_id" }, { key: "msisdn", label: "号码" }, { key: "mcc", label: "MCC" }, { key: "mnc", label: "MNC" },
  { key: "minAttempts", label: "最少投递次数" }, { key: "customer", label: "客户" }, { key: "account", label: "账号" }, { key: "channel", label: "通道" },
  { key: "finalChannel", label: "最终通道" }, { key: "status", label: "状态" }, { key: "aggregate", label: "重投聚合" },
];
const FILTER_KEYS = FILTERS.map((f) => f.key);
const DEFAULT_FILTERS: FilterKey[] = ["range", "msisdn", "mcc", "mnc", "account", "channel", "status"];
const COLUMN_KEYS = MESSAGE_COLUMNS.map((c) => c.key);

/** 把 o 里属于 keys 的条件清空。 */
function without<T extends Record<string, string>>(o: T, keys: FilterKey[]): T {
  return Object.fromEntries(Object.entries(o).map(([k, v]) => [k, keys.includes(k as FilterKey) ? "" : v])) as T;
}

/** 消息记录：按 message_id、号码、客户、账号、通道（任一次尝试）、最终通道（最新一次尝试）、MCC、MNC、最少投递次数、状态与时间范围查（docs/04 第 8 节），通道列是最新一次尝试的通道，投递次数单列一栏（#204）；点开看基本信息、客户回执与提交记录（#213）。缺省最近 12 小时、最长 90 天；服务端从最新往前分段查、每次限时，一页没凑满也会给出下一页的起点，翻下一页接着查（决策 D82）。
 * 筛选条件可在“筛选条件”里选择显示哪些，隐藏的条件同时清空；重投可以不聚合，每次尝试一行，仍按消息分页与计数（#513，决策 D187）。表格列可在“显示列”里选择（决策 D198）。
 * 右上角按已生效的条件导出 Excel：选字段建任务，任务在后台执行，顶栏的任务中心里看进度与下载（决策 D202、D205）。 */
export function Messages() {
  const { range, setRange, span, refresh } = useRange(DEFAULT_RANGE);
  // 文本框要回车或点"查询"才生效（applied），下拉框选了就生效；清除筛选时两边一起回到 EMPTY
  const [draft, setDraft] = useState(EMPTY_TEXT);
  const [applied, setApplied] = useState(EMPTY_TEXT);
  const [picked, setPicked] = useState(EMPTY_PICK);
  const [open, setOpen] = useState<string | null>(null);
  const [invalid, setInvalid] = useState("");
  // 导出：选字段的表单，建好后打开顶栏的任务中心看进度（决策 D202、D205）
  const [exporting, setExporting] = useState(false);
  const canWrite = useCanWrite();
  const taskCenter = useTaskCenter();
  const [shown, setShown] = useShown<FilterKey>("filters", "messages", FILTER_KEYS, DEFAULT_FILTERS);
  const [columns, setColumns] = useShown<MessageColumn>("columns", "messages", COLUMN_KEYS, COLUMN_KEYS);
  const accountList = useList("accounts", accounts);
  const customerList = useList("customers", customers);
  const names = useNames();
  const channelList = useList("channels", channels);
  const from = span?.from.toISOString() ?? "";
  const to = span?.to.toISOString() ?? "";
  const filter = { message_id: applied.messageId, msisdn: applied.msisdn, customer_id: picked.customer, account_id: picked.account, channel_id: picked.channel,
    final_channel_id: picked.finalChannel, mcc: applied.mcc, mnc: applied.mnc, min_attempts: applied.minAttempts, status: picked.status, from, to };
  // 不聚合只影响列表的展示，计数仍数消息，不进计数的条件
  const perAttempt = picked.aggregate === "each";
  const listFilter = { ...filter, per_attempt: perAttempt ? "true" : "" };
  const tooWide = !!span && span.to.getTime() - span.from.getTime() > MAX_RANGE_MS;
  const pager = useNextPage<MessageCursor>("messages", listFilter);
  const page = { limit: pager.limit, to: pager.cursor?.to ?? to, before_id: pager.cursor?.before_id };
  // 换条件时取消上一次请求，服务端随之释放宽查询的槽位；超时或正忙的 503 不自动重试，重跑一次同样要扫几秒（决策 D82）
  const list = useQuery({ queryKey: ["messages", listFilter, page], queryFn: ({ signal }) => messages.list({ ...listFilter, ...page }, signal), enabled: !!span && !tooWide, placeholderData: keepPreviousData, retry: false });
  const data = tooWide ? undefined : list.data; // 范围太大时不显示上一次查询的占位数据
  // 总数只按条件取（staleTime 无限：翻页时 enabled 来回切换也不重取，点“查询”经 refresh 换了 to 就是新键）；等列表返回后再取，
  // 宽条件的计数与列表共用服务端的同一个槽位（决策 D182）
  const qc = useQueryClient();
  const countKey = ["messages", "count", filter];
  const total = useQuery({ queryKey: countKey, queryFn: ({ signal }) => messages.count(filter, signal), enabled: !!span && !tooWide && !list.isFetching, retry: false, staleTime: Infinity });
  const view = pager.view(data?.rows ?? [], data?.next);
  // 翻页前取消还在途的计数：不取消的话下一页会撞上它占着的槽位、拿到 503；取消了等下一页返回再数一次
  const onChange = (p: number) => { void qc.cancelQueries({ queryKey: countKey, exact: true }); view.onChange(p); };
  const paged = { ...view, onChange, ...totalOf(tooWide ? undefined : total.data), unit: perAttempt ? "条消息" : "条" };
  const rows = paged.rows;
  const lines = messageLines(rows, perAttempt, picked.channel);
  // 一页没凑满又有下一页：服务端用完了这次的扫描预算，告诉用户查到了哪里
  const partial = data?.next && rows.length < pager.limit ? data.next.to : null;
  const text = (key: keyof typeof EMPTY_TEXT) => ({ value: draft[key], onChange: (e: ChangeEvent<HTMLInputElement>) => setDraft({ ...draft, [key]: e.target.value }), onKeyDown: (e: KeyboardEvent) => e.key === "Enter" && search() });
  const pick = (key: keyof typeof EMPTY_PICK) => ({ value: picked[key], onChange: (v: string) => { setPicked({ ...picked, [key]: v }); refresh(); } });
  const search = () => {
    const t = { messageId: draft.messageId.trim(), msisdn: draft.msisdn.trim().replace(/^\+/, ""), mcc: draft.mcc.trim(), mnc: draft.mnc.trim(), minAttempts: draft.minAttempts.trim() };
    // 格式与服务端一致（docs/04 第 8 节），不对就在这里提示，不发请求
    if ((t.mcc && !validMcc(t.mcc)) || (t.mnc && !validMnc(t.mnc))) {
      setInvalid("MCC 是三位数字，MNC 是两到三位数字。");
      return;
    }
    if (t.minAttempts && !/^[1-9]\d{0,3}$/.test(t.minAttempts)) {
      setInvalid("最少投递次数是正整数。");
      return;
    }
    setInvalid("");
    setApplied(t);
    refresh();
  };
  // 隐藏的条件同时清空，免得看不见的条件还在起作用
  const showFilters = (keys: FilterKey[]) => {
    const hidden = shown.filter((k) => !keys.includes(k));
    setShown(keys);
    if (!hidden.length) return;
    setDraft((d) => without(d, hidden));
    setApplied((a) => without(a, hidden));
    setPicked((p) => without(p, hidden));
    setInvalid("");
    if (hidden.includes("range")) setRange(DEFAULT_RANGE);
  };
  const on = (k: FilterKey) => shown.includes(k);
  // 不聚合时投递次数列是这一次的序号，表头与“显示列”里同名
  const colLabel = (c: (typeof MESSAGE_COLUMNS)[number]) => (c.key === "attempts" && perAttempt ? "第几次" : c.label);
  // 有任何条件（含没提交的输入、非缺省时间范围）才显示清除筛选
  const filtered = [draft, applied, picked].some((o) => Object.values(o).some(Boolean)) || !("preset" in range && range.preset === DEFAULT_RANGE.preset);
  // 清除后按钮随即消失，焦点交给“查询”
  const queryButton = useRef<HTMLButtonElement>(null);
  const clear = () => {
    setDraft(EMPTY_TEXT);
    setApplied(EMPTY_TEXT);
    setPicked(EMPTY_PICK);
    setInvalid("");
    setRange(DEFAULT_RANGE);
  };

  return (
    <>
      <PageHeader title="消息记录" description="按号码、账号、通道、状态与时间等条件查询消息；通道按任一次尝试匹配，最终通道按最新一次。"
        actions={canWrite && <Button variant="primary" disabled={!span || tooWide} onClick={() => setExporting(true)}>导出 Excel</Button>} />
      {invalid && <Alert tone="warning" title="筛选条件不对">{invalid}</Alert>}
      {tooWide && <Alert tone="warning" title="时间范围太大">最长查 90 天。</Alert>}
      {partial && <Alert title="还没查完">已查到 {formatTime(partial)}，翻到下一页接着往前查。</Alert>}
      {list.error && <Alert tone="danger" title="加载失败">{list.error.message}</Alert>}
      {/* 筛选条件、显示列、查询一组跟在最后一个筛选控件后（换行时整组一起换）：都是只有图标的按钮，筛选条件选择显示哪些条件，显示列选择表格显示哪些列；
          有条件时查询左侧再出现只有图标的清除筛选，一次清空全部条件并回到缺省时间范围（决策 D198）；页码在表格底部的分页栏里 */}
      <Toolbar actions={<>
        <ShowPicker label="筛选条件" icon={<ToolIcon name="filter" />} heading="显示的筛选条件" options={FILTERS} shown={shown} onChange={showFilters} />
        <ShowPicker label="显示列" icon={<ToolIcon name="columns-3" />} heading="显示的列" options={MESSAGE_COLUMNS.map((c) => ({ key: c.key, label: colLabel(c) }))} shown={columns} onChange={setColumns} />
        {filtered && <ClearFilters onClear={clear} focus={queryButton} />}
        <Button ref={queryButton} size="sm" onClick={search}>查询</Button>
      </>}>
        {on("range") && <DateRange size="sm" value={range} onChange={setRange} />}
        {on("messageId") && <Input size="sm" mono placeholder="message_id" aria-label="message_id" style={{ width: FILTER_W.id }} {...text("messageId")} />}
        {on("msisdn") && <Input size="sm" mono placeholder="号码" aria-label="号码" style={{ width: FILTER_W.msisdn }} {...text("msisdn")} />}
        {on("mcc") && <Input size="sm" mono placeholder="MCC" aria-label="MCC" style={{ width: FILTER_W.code }} {...text("mcc")} />}
        {on("mnc") && <Input size="sm" mono placeholder="MNC" aria-label="MNC" style={{ width: FILTER_W.code }} {...text("mnc")} />}
        {on("minAttempts") && <Input size="sm" mono placeholder="最少投递次数" aria-label="最少投递次数" style={{ width: FILTER_W.num }} {...text("minAttempts")} />}
        {on("customer") && <Combobox size="sm" width={FILTER_W.select} ariaLabel="客户" searchPlaceholder="搜索客户名称" options={[{ value: "", label: "全部客户" }, ...idOptions(customerList.data)]} {...pick("customer")} />}
        {on("account") && <Combobox size="sm" width={FILTER_W.select} ariaLabel="账号" searchPlaceholder="搜索账号名称或 system_id" options={[{ value: "", label: "全部账号" }, ...accountOptions(accountList.data, customerList.data)]} {...pick("account")} />}
        {on("channel") && <Combobox size="sm" width={FILTER_W.select} ariaLabel="通道" searchPlaceholder="搜索通道名称" options={[{ value: "", label: "全部通道" }, ...idOptions(channelList.data)]} {...pick("channel")} />}
        {on("finalChannel") && <Combobox size="sm" width={FILTER_W.select} ariaLabel="最终通道" searchPlaceholder="搜索通道名称" options={[{ value: "", label: "全部最终通道" }, ...idOptions(channelList.data)]} {...pick("finalChannel")} />}
        {on("status") && <Select size="sm" width={FILTER_W.select} ariaLabel="状态" options={STATUS_OPTIONS} {...pick("status")} />}
        {on("aggregate") && <Select size="sm" width={FILTER_W.select} ariaLabel="重投聚合" options={AGGREGATE_OPTIONS} {...pick("aggregate")} />}
      </Toolbar>
      <TableWrap loading={!tooWide && loadingOf(list)}>
        {rows.length || paged.pageCount > paged.page ? (
          <>
          <Table dense striped>
            <thead><tr>
              {MESSAGE_COLUMNS.filter((c) => columns.includes(c.key)).map((c) => <Th key={c.key} numeric={"numeric" in c} w={c.w}>{colLabel(c)}</Th>)}
              <Th />
            </tr></thead>
            <tbody>
              {lines.map((l) => <MessageLineRow key={l.a?.id ?? l.m.id} {...l} names={names} channels={channelList.data} columns={columns} onOpen={setOpen} />)}
            </tbody>
          </Table>
          <Pager {...paged} />
          </>
        ) : (
          (tooWide || !list.isPending) && <EmptyState title={tooWide ? "没有查询" : "没有消息"}>{tooWide ? "把时间范围缩到 90 天以内。" : "范围内没有匹配的消息。"}</EmptyState>
        )}
      </TableWrap>
      {open && <MessageDetail id={open} channels={channelList.data} onClose={() => setOpen(null)} />}
      {exporting && <MessageExportForm filter={filter} onClose={() => setExporting(false)} onCreated={() => { setExporting(false); taskCenter.open(); }} />}
    </>
  );
}
